/**
 * OCR — lecture d'une photo de menu ou d'un PDF scanné (Brique 3·C, extension).
 *
 * Le restaurateur n'a parfois qu'une PHOTO de sa carte. On la passe à l'OCR
 * (Tesseract, en français), on nettoie le texte, puis on réutilise le PARSEUR
 * de menu existant (menuImport.ts). Tesseract est importé dynamiquement → pas
 * chargé tant qu'on ne lance pas d'OCR (et jamais en test).
 *
 * Séparation :
 *  - `cleanOcrText` : LOGIQUE PURE (testable, sans Tesseract).
 *  - le reste : I/O navigateur (Tesseract + canvas).
 */

/**
 * Nettoie une sortie OCR brute pour la rendre exploitable par le parseur :
 *  - normalise les espaces, supprime les lignes vides,
 *  - retire les lignes « bruit » (aucune lettre ni chiffre),
 *  - rapproche un prix collé au nom reste géré par le parseur.
 */
export function cleanOcrText(raw: string): string {
  return raw
    .split('\n')
    .map((line) => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line) => line !== '')
    .filter((line) => /[a-zA-Zà-üÀ-Ü0-9]/.test(line)) // au moins une lettre ou un chiffre
    .join('\n')
    .trim();
}

/** Étape de progression OCR (pour l'UI), 0..1. */
export type OcrProgress = (ratio: number) => void;

/**
 * Reconnaît le texte d'une image (Blob ou canvas/URL) via Tesseract (français).
 * Renvoie un texte déjà nettoyé.
 */
export async function recognizeImage(
  source: Blob | HTMLCanvasElement | string,
  onProgress?: OcrProgress,
): Promise<string> {
  const { createWorker } = await import('tesseract.js');
  const worker = await createWorker('fra', undefined, {
    logger: (m: { status: string; progress: number }) => {
      if (m.status === 'recognizing text' && onProgress) onProgress(m.progress);
    },
  });
  try {
    const { data } = await worker.recognize(source);
    return cleanOcrText(data.text);
  } finally {
    await worker.terminate();
  }
}

/** OCR d'un fichier image (jpg/png/…). */
export async function extractTextFromImageFile(file: Blob, onProgress?: OcrProgress): Promise<string> {
  return recognizeImage(file, onProgress);
}

/**
 * OCR d'un PDF scanné : rend chaque page en image (pdf.js) puis OCR.
 * Plus lent (1 passe OCR par page). Utilisé en repli quand un PDF n'a pas de texte.
 */
export async function extractTextFromScannedPdf(data: ArrayBuffer, onProgress?: OcrProgress): Promise<string> {
  const pdfjs = await import('pdfjs-dist');
  const workerUrl = (await import('pdfjs-dist/build/pdf.worker.min.mjs?url')).default;
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const doc = await pdfjs.getDocument({ data }).promise;
  const pages: string[] = [];
  for (let p = 1; p <= doc.numPages; p++) {
    const page = await doc.getPage(p);
    const viewport = page.getViewport({ scale: 2 }); // x2 pour une meilleure lecture OCR
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) continue;
    await page.render({ canvasContext: ctx, viewport }).promise;
    const text = await recognizeImage(
      canvas,
      onProgress ? (r) => onProgress((p - 1 + r) / doc.numPages) : undefined,
    );
    if (text.trim()) pages.push(text);
  }
  return pages.join('\n').trim();
}
